import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';

import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import { Avatar, IconCollection } from '../../../../components';
import {
  abilityToSell,
  CURRENT_USER_BUSINESS_TYPE,
  GROUP_PAYMENT_PAGE_PAYMENT_HISTORY,
  PRIMARY_RULEBOOK_GROUP_ID,
  SIGNUP_TAB_DETAIL,
  USER_TYPE_YOUTH,
  YOUTH_AGE_LIMIT,
} from '../../../../util/types';
import css from './MobileHamburgerNavigation.module.css';
import MenuIcon from '../MenuIcon';
import classNames from 'classnames';
import { createResourceLocatorString } from '../../../../util/routes';
import { openIntecomOnClickEvents } from '../../../../components/IntercomComponent/IntercomComponent';
import {
  fetchBookMarList,
  fetchUserActivity,
  toggleCreatePost,
} from '../../../CircleCommunityPage/CircleCommunityPage.duck';
import { useDispatch } from 'react-redux';
import { subscriptionPermissionRequest, requestGroupPermissionModal, setElephantVerificationModal } from '../../../../ducks/user.duck';
import {
  calculateAge,
  checkElephantModalOpen,
  currentUserRole,
  getFavoriteRuleBooks,
  getLastThreeRecentChats,
  getUserPermissions,
  isUserSyncShopify,
} from '../../../../util/data';
import { parse } from '../../../../util/urlHelpers';

const REDIRECTION_TYPE_SUB_TABS = 'SUB_TABS';
const REDIRECTION_TYPE_LINK = 'LINK';
const REDIRECTION_TYPE_BUTTON = 'BUTTON';

const SORT_ORDER_KEY = 'joinedGroupsSortOrder';

// Returns the tab key for the section in menuData that contains currentPage in its pages array
function getSubMenu(menuData, currentPage) {
  if (!Array.isArray(menuData) || !currentPage) return '';
  for (const section of menuData) {
    if (Array.isArray(section.pages) && section.pages.includes(currentPage)) {
      return section;
    }
  }
  return null;
}

const categories = [
  {
    text: 'Equipment',
    tab: 'REDIRECTION_TYPE_SUB_TABS_EQUIPMENT',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Equipment',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For Riding',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR RIDING',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For Riding',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For The Barn',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR THE BARN',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For The Barn',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For The Horse',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR THE HORSE',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For The Horse',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'For The Rider',
    tab: 'REDIRECTION_TYPE_SUB_TABS_FOR THE RIDER',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'For The Rider',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'Podcasts',
    tab: 'REDIRECTION_TYPE_SUB_TABS_PODCASTS',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Podcasts',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'Services',
    tab: 'REDIRECTION_TYPE_SUB_TABS_SERVICES',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Services',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
  {
    text: 'Other',
    tab: 'REDIRECTION_TYPE_SUB_TABS_OTHER',
    pageName: 'SearchPage',
    params: {},
    search: {
      hierarchicalCategories: 'Other',
    },
    redirectionType: REDIRECTION_TYPE_LINK,
  },
];

const MobileHamburgerNavigation = ({
  currentUser,
  notificationCount = 0,
  currentUserUnreadInquiriesCount = 0,
  cartCount = 0,
  history,
  routeConfiguration,
  onLogout,
  spaceListData,
  userModeratorsGroups,
  setIsBookmarkListRequested,
  currentCommunityMember,
  setIsMyActivityModalOpen,
  checkCreatePostAuth,
  checkCreateGroupAuth,
  onRequestGroupClick,
  onOpenRuleBookModal,
  userFavChatDetails,
  chatDetails,
  openNotificationModal,
  notificationCountData,
  currentPage,
  onFetchNotificationsCount,
  onToggleCreatePost,
  isSiderbarOpen = false,
  onSiderbarChange,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id: community_member_id } = currentCommunityMember || {};

  const userId = currentUser?.id?.uuid;

  // Check if user is on their own profile page
  const isOnOwnProfile = currentPage === 'ProfilePage' && userId &&
    typeof window !== 'undefined' &&
    window.location.pathname.includes(`/u/${userId}`);
  const { publicData, protectedData, privateData } =
    (currentUser && currentUser.id && currentUser.attributes?.profile) || {};
  const { userRole, parentId = false } = publicData || {};
  const { subscriptionData = false } = protectedData || {};
  const { isOrganizationAdmin, isSupabaseAdmin, isWalletAdmin, isModAdmin, isUserAdmin, isShopifyAdmin, isHighlightAdmin, isDiscoverGroupsCarouselAdmin, isSubscriptionAdmin, isDiscountAdmin } =
    privateData || {};
  const userSubscriptionRole = currentUserRole(currentUser);
  const userAbilityToSell = getUserPermissions(userSubscriptionRole, abilityToSell);
  const isShopifySyncEnabled = isUserSyncShopify(currentUser);

  const isUserEmailVerified = currentUser?.attributes?.emailVerified;
  const isOpenElephantModal = checkElephantModalOpen(currentUser);
  const isSeller =
    userRole !== USER_TYPE_YOUTH && (subscriptionData || !parentId) && userAbilityToSell;
  const isYouthRestricted = userRole === USER_TYPE_YOUTH && parentId && !subscriptionData;

  const favouriteRuleBook = getFavoriteRuleBooks(userFavChatDetails);
  const recentChats = getLastThreeRecentChats(chatDetails);

  const isBusiness = publicData?.currentUserType === CURRENT_USER_BUSINESS_TYPE;

  // Calculate user age from DOB
  const userDob = publicData?.dob;
  const userAge = userDob ? calculateAge(userDob) : null;
  const isUserAdult = userAge !== null && userAge >= YOUTH_AGE_LIMIT;

  // Check if user should see upgrade button (16+ and not basic role)
  const shouldShowUpgradeButton = isUserAdult && userRole !== 'basic';

  // Calculate total notification count (same as desktop)
  const { new_notifications_count = 0, new_mentions_count = 0 } = notificationCountData || {};
  const totalNotificationCount = new_notifications_count + new_mentions_count;

  // states
  // Single state to track expanded tab: null, 'COMMUNITY_MY_GROUP', or 'COMMUNITY_MODERATED_GROUP'
  const [expandedTab, setExpandedTab] = useState(null);

  // Sort order states with sessionStorage persistence
  const [joinedGroupsSortOrder, setJoinedGroupsSortOrder] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(SORT_ORDER_KEY) || 'asc';
    }
    return 'asc';
  });
  const [subMenuOpen, setSubMenuOpen] = useState(null);
  const [query, setQuery] = useState('');
  const [joinedGroupsQuery, setJoinedGroupsQuery] = useState('');

  // use effect
  useEffect(() => {
    if (isSiderbarOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open'); // cleanup
    };
  }, [isSiderbarOpen]);

  useEffect(() => {
    if (isSiderbarOpen) return;
    setSubMenuOpen(null);
    setExpandedTab(null);
  }, [isSiderbarOpen]);

  // Fetch notification count when component mounts or user logs in
  useEffect(() => {
    if (userId && onFetchNotificationsCount) {
      onFetchNotificationsCount({ forceFresh: true });
    }
  }, [userId]);

  const aiFavouriteRuleBook = favouriteRuleBook.map(st => {
    return {
      text: st.rulebook_name,
      tab: `COMMUNITY_AI_FAVOURITE_${st.id}`,
      pageName: 'AiChatBotRulebookPage',
      params: { rulebookId: st.rulebook_id, sessionId: uuidv4() },
      search: {},
      redirectionType: REDIRECTION_TYPE_LINK,
      Icon: <IconCollection name={'roundIcon'} />,
    };
  });

  const aiRecentRuleBook = recentChats.map(st => {
    return {
      text: st.query,
      tab: `COMMUNITY_AI_RECENT_${st.id}`,
      pageName: 'AiChatBotRulebookPage',
      params: { rulebookId: st.rulebook_id, sessionId: st.sessionId },
      search: {},
      redirectionType: REDIRECTION_TYPE_LINK,
      Icon: <IconCollection name={'recentRoundIcon'} />,
    };
  });

  const moderatorGroupsRaw = Array.isArray(spaceListData)

    ? spaceListData
      .filter(item => {
        const isModerator =
          Array.isArray(userModeratorsGroups) &&
          userModeratorsGroups.some(st => st.status == 'active' && st.groupId == item.id);
        const canManage = item.is_member;
        return isModerator && canManage;
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(st => {
        return {
          text: st.name,
          tab: `COMMUNITY_GROUP_EXPLORE_${st.id}`,
          pageName: 'JoinedGroupsCommunityPage',
          params: { id: st.id, slug: st.slug },
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
          Icon: <ProfileIconCard type="green" />,
          notificationCount: st.pendingCount || 0,
        };
      })
    : [];

  const moderatorGroups =
    moderatorGroupsRaw.length > 4 && expandedTab !== 'COMMUNITY_GROUP_EXPLORE_SEE_MORE'
      ? [
        ...moderatorGroupsRaw.slice(0, 4),
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.SEE_MORE',
            defaultMessage: 'See more',
          }),
          tab: 'COMMUNITY_GROUP_EXPLORE_SEE_MORE',
          type: 'Button',
        },
      ]
      : moderatorGroupsRaw.length > 4
        ? [
          ...moderatorGroupsRaw,
          {
            text: intl.formatMessage({
              id: 'MobileHamburgerNavigation.SEE_LESS',
              defaultMessage: 'See less',
            }),
            tab: 'COMMUNITY_MY_GROUP_SEE_MORE',
            type: 'Button',
          },
        ]
        : moderatorGroupsRaw;

  const JoinedGroupsRaw = Array.isArray(spaceListData)
    ? spaceListData
      .filter(item => {
        const isModerator =
          Array.isArray(userModeratorsGroups) &&
          userModeratorsGroups.some(st => st.status == 'active' && st.groupId == item.id);
        const canManage = item.is_member;
        return !isModerator && canManage;
      })
      .sort((a, b) => joinedGroupsSortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
      )
      .map(st => {
        return {
          text: st.name,
          tab: `COMMUNITY_MY_GROUP_${st.id}`,
          pageName: 'JoinedGroupsCommunityPage',
          params: { id: st.id, slug: st.slug },
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
          Icon: <ProfileIconCard type="orange" />,
        };
      })
    : [];
  // Apply search filter to JoinedGroupsRaw so mobile menu reflects query immediately
  const JoinedGroupsFiltered = typeof joinedGroupsQuery === 'string' && joinedGroupsQuery.trim()
    ? JoinedGroupsRaw.filter(item => (item.text || '').toLowerCase().includes(joinedGroupsQuery.toLowerCase()))
    : JoinedGroupsRaw;

  // Check if we should show "No Result" message
  const shouldShowNoResult = joinedGroupsQuery.trim() && JoinedGroupsFiltered.length === 0;

  const JoinedGroups = shouldShowNoResult
    ? [
      {
        text: intl.formatMessage({
          id: 'MobileHamburgerNavigation.NO_GROUPS_FOUND',
          defaultMessage: 'No groups found',
        }),
        tab: 'NO_RESULT',
        type: 'NoResult',
      },
    ]
    : JoinedGroupsFiltered.length > 4 && expandedTab !== 'COMMUNITY_MY_GROUP_SEE_MORE'
      ? [
        ...JoinedGroupsFiltered.slice(0, 4),
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.SEE_MORE',
            defaultMessage: 'See more',
          }),
          tab: 'COMMUNITY_MY_GROUP_SEE_MORE',
          type: 'Button',
        },
      ]
      : JoinedGroupsFiltered.length > 4
        ? [
          ...JoinedGroupsFiltered,
          {
            text: intl.formatMessage({
              id: 'MobileHamburgerNavigation.SEE_LESS',
              defaultMessage: 'See less',
            }),
            tab: 'COMMUNITY_MY_GROUP_SEE_MORE',
            type: 'Button',
          },
        ]
        : JoinedGroupsFiltered;

  const menuData = [
    {
      Icon: <ProfileIconCard type="community_bottom_mobile_svg" />,
      tab: 'COMMUNITY',
      label: userId ? intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_WITH_USER' }) : intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_SUB' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: userId ? 'CircleCommunityPage' : 'GroupSearchPage',
      params: {},
      search: {},
      tabLevel: 1,
      subTabs: [
        {
          Icon: <ProfileIconCard type="community_feed_tab" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_MY_FEED' }),
          tab: 'COMMUNITY_FEED_TAB',
          redirectionType: REDIRECTION_TYPE_LINK,
          pageName: 'CircleCommunityPage',
          params: {},
          search: {},
        },
        {
          Icon: <ProfileIconCard type="community_explore" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_EXPLORE' }),
          tab: 'COMMUNITY_EXPLORE',
          redirectionType: REDIRECTION_TYPE_LINK,
          pageName: 'GroupSearchPage',
          params: {},
          search: {},
        },
        ...(userId
          ? [
            {
              Icon: <ProfileIconCard type="community_saved_post" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_SAVED_POST' }),
              tab: 'COMMUNITY_SAVED_POST',
              redirectionType: REDIRECTION_TYPE_BUTTON,
              onClick: () => {
                onSiderbarChange(false);
                if (!isUserEmailVerified) {
                  return history.push(
                    createResourceLocatorString(
                      'SignUpStepsPage',
                      routeConfiguration,
                      { tab: SIGNUP_TAB_DETAIL },
                      {}
                    )
                  );
                }
                setIsBookmarkListRequested(true);
                dispatch(fetchBookMarList({}));
              },
            },
            {
              Icon: <ProfileIconCard type="community_activity" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_ACTIVITY' }),
              tab: 'COMMUNITY_ACTIVITY',
              redirectionType: REDIRECTION_TYPE_BUTTON,
              onClick: () => {
                if (!isUserEmailVerified) {
                  onSiderbarChange(false);
                  return history.push(
                    createResourceLocatorString(
                      'SignUpStepsPage',
                      routeConfiguration,
                      { tab: SIGNUP_TAB_DETAIL },
                      {}
                    )
                  );
                }
                if (!community_member_id) return;
                onSiderbarChange(false);
                setIsMyActivityModalOpen(true);
                dispatch(fetchUserActivity({ community_member_id, fetchTab: 'posts' }));
              },
            },
          ]
          : []),
        {
          text: intl.formatMessage({
            id: 'MobileHamburgerNavigation.COMMUNITY_REQUEST_GROUP_BUTTON',
          }),
          tab: 'COMMUNITY_REQUEST_GROUP_BUTTON',
          pageName: false,
          type: 'Button',
          disabled: false,
          redirectionType: REDIRECTION_TYPE_BUTTON,
          onClick: () => {
            if (!userId) {
              onSiderbarChange(false);
              return history.push(
                createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
              );
            }
            if (!isUserEmailVerified) {
              return history.push(
                createResourceLocatorString(
                  'SignUpStepsPage',
                  routeConfiguration,
                  { tab: SIGNUP_TAB_DETAIL },
                  {}
                )
              );
            }

            if (isOpenElephantModal) {
              dispatch(setElephantVerificationModal(true));
              return;
            }
            if (!checkCreateGroupAuth) {
              dispatch(requestGroupPermissionModal(true));
              return;
            }
            onRequestGroupClick(true);
            onSiderbarChange(false);
          },
        },
        ...(userId
          ? [
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.COMMUNITY_CREATE_POST_BUTTON',
              }),
              tab: 'COMMUNITY_CREATE_POST_BUTTON',
              pageName: false,
              type: 'CreatePostButton',
              disabled: false,
              redirectionType: REDIRECTION_TYPE_BUTTON,
              onClick: () => {
                if (!isUserEmailVerified) {
                  return history.push(
                    createResourceLocatorString(
                      'SignUpStepsPage',
                      routeConfiguration,
                      { tab: SIGNUP_TAB_DETAIL },
                      {}
                    )
                  );
                }
                if (!checkCreatePostAuth) {
                  dispatch(subscriptionPermissionRequest(true));
                  return;
                }
                if (isOpenElephantModal) {
                  dispatch(setElephantVerificationModal(true));
                  return;
                }
                onSiderbarChange(false);

                // If we're already on CircleCommunityPage or MyGroupCommunityPage, just open the modal
                if ((currentPage === 'CircleCommunityPage' || currentPage === 'MyGroupCommunityPage' || currentPage === 'JoinedGroupsCommunityPage') && onToggleCreatePost) {
                  onToggleCreatePost();
                } else {
                  // Navigate to CircleCommunityPage with createPost query param
                  history.push(
                    createResourceLocatorString('CircleCommunityPage', routeConfiguration, {}, { createPost: 'true' })
                  );
                }
              },
            },
          ]
          : []),
        {
          text: '',
          tab: 'UNDERLINE',
        },
        ...(userId
          ? [
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.COMMUNITY_MODERATED_GROUP',
              }),
              tab: 'COMMUNITY_MODERATED_GROUP',
              redirectionType: REDIRECTION_TYPE_SUB_TABS,
              subTabs: moderatorGroups,
              isLast: true,
              tabLevel: 2,
            },
            {
              text: '',
              tab: 'UNDERLINE',
            },
            {
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.COMMUNITY_MY_GROUP' }),
              tab: 'COMMUNITY_MY_GROUP',
              pageName: false,
              subTabs: JoinedGroups,
              searchControl: true,
              isLast: true,
              tabLevel: 2,
              sortButton: {
                text: joinedGroupsSortOrder === 'asc' ? 'A-Z' : 'Z-A',
                onClick: () => {
                  const newOrder = joinedGroupsSortOrder === 'asc' ? 'desc' : 'asc';
                  setJoinedGroupsSortOrder(newOrder);
                  sessionStorage.setItem(SORT_ORDER_KEY, newOrder);
                },
              },
            },
          ]
          : [
            {
              Icon: <ProfileIconCard type="join_now" />,
              tab: 'JOIN_NOW',
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.JOIN_NOW' }),
              pageName: 'SignupPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
            {
              Icon: <ProfileIconCard type="join_now" />,
              tab: 'LOGIN',
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.LOGIN' }),
              pageName: 'LoginPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
          ]),
      ],
      pages: [
        'CircleCommunityPage',
        'ExploreCommunityPage',
        'MyGroupCommunityPage',
        'DiscoverGroupsCommunityPage',
        'JoinedGroupsCommunityPage',
        'PostViewPage',
        'GroupSearchPage',
      ],
    },
    {
      Icon: <ProfileIconCard type="marketplace_bottom_mobile_svg" />,
      tab: 'MARKETPLACE',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_SUB' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: 'SearchPage',
      params: {},
      search: {},
      tabLevel: 1,
      subTabs: [
        {
          Icon: <ProfileIconCard type="marketplace_search_page" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_SEARCH' }),
          tab: 'MARKETPLACE_SERACH_PAGE',
          pageName: 'SearchPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        ...(!isYouthRestricted
          ? [
            {
              Icon: <ProfileIconCard type="marketplace_cart_page" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_CART_PAGE' }),
              tab: 'MARKETPLACE_CART_PAGE',
              notificationCount: cartCount,
              pageName: userId ? 'CartPage' : false,
              params: {},
              search: {},
              redirectionType: userId ? REDIRECTION_TYPE_LINK : REDIRECTION_TYPE_BUTTON,
              onClick: !userId
                ? () => {
                  onSiderbarChange(false);
                  return history.push(
                    createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
                  );
                }
                : undefined,
            },
          ]
          : []),
        ...(userAbilityToSell || isShopifySyncEnabled
          ? [
            {
              Icon: <ProfileIconCard type="marketplace_manage_listings_page" />,
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.MARKETPLACE_MANAGE_LISTINGS_PAGE',
              }),
              tab: 'MARKETPLACE_MANAGE_LISTINGS_PAGE',
              pageName: 'ManageListingsPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
          ]
          : []),
        ...(!isYouthRestricted && userId
          ? [
            {
              Icon: <ProfileIconCard type="marketplace_inbox_base_page" />,
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.MARKETPLACE_INBOX_BASE_PAGE',
              }),
              tab: 'MARKETPLACE_INBOX_BASE_PAGE',
              pageName: 'InboxBasePage',
              notificationCount: notificationCount + currentUserUnreadInquiriesCount || 0,
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
          ]
          : []),
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_POST_ITEM' }),
          tab: 'MARKETPLACE_POST_ITEM',
          type: 'Button',
          disabled: false,
          onClick: () => {
            onSiderbarChange(false);
            if (!userId) {
              return history.push(
                createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
              );
            }
            if (!userAbilityToSell) {
              dispatch(subscriptionPermissionRequest(true, true));
              return null;
            }
            return history.push(
              createResourceLocatorString('NewListingPage', routeConfiguration, {}, {})
            );
          },
          redirectionType: REDIRECTION_TYPE_BUTTON,
        },
        // ...(!userId || isShopifySyncEnabled
        //   ? [
        //       {
        //         text: intl.formatMessage({
        //           id: 'MobileHamburgerNavigation.SHOPIFY_SYNC_BUTTON',
        //         }),
        //         tab: 'SHOPIFY_SYNC_BUTTON',
        //         pageName: false,
        //         type: 'ShopifyButton',
        //         disabled: false,
        //         redirectionType: REDIRECTION_TYPE_BUTTON,
        //         onClick: () => {
        //           onSiderbarChange(false);
        //           if (!userId) {
        //             return history.push(
        //               createResourceLocatorString('SignupPage', routeConfiguration, {}, {})
        //             );
        //           }
        //           if (!isShopifySyncEnabled) {
        //             return null;
        //           }
        //           return history.push(
        //             createResourceLocatorString(
        //               'ShopifyOnboardingPageTab',
        //               routeConfiguration,
        //               { tab: 'connect' },
        //               {}
        //             )
        //           );
        //         },
        //       },
        //     ]
        //   : []),
        {
          text: '',
          tab: 'UNDERLINE',
        },
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.MARKETPLACE_MAIN_CATEGORIES' }),
          tab: 'MARKETPLACE_MAIN_CATEGORIES',
          redirectionType: REDIRECTION_TYPE_SUB_TABS,
          subTabs: categories,
          isLast: true,
          tabLevel: 2,
        },
      ],
      pages: [
        'SearchPage',
        'ListingPage',
        'CheckoutPage',
        'ListingPageVariant',
        'EditListingPage',
        'ListingPageCanonical',
        'InboxPage',
        'TransactionPage',
        'OrderDetailsPage',
        'SaleDetailsPage',
        'ManageListingsPage',
        'CartPage',
        'SubscriptionCheckoutPage',
        'GroupPaymentSuccessPage',
      ],
    },
    {
      Icon: <ProfileIconCard type="turnoutai_bottom_mobile_svg" />,
      tab: 'AI',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI' }),
      subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_SUB' }),
      redirectionType: REDIRECTION_TYPE_SUB_TABS,
      tabLevel: 1,
      subTabs: [
        {
          Icon: <ProfileIconCard type="marketplace_search_page" />,
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_RULEBOOKS' }),
          tab: 'AI_RULEBOOKS',
          pageName: 'RuleBooksSearchPage',
          params: { rulebookId: 'all' },
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        ...(userId
          ? [
            {
              Icon: <ProfileIconCard type="community_saved_post" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_FAVOURITES' }),
              tab: 'AI_EXPLORE',
              pageName: 'FavouritePage',
              params: { rulebookId: 'favourites' },
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
            {
              Icon: <ProfileIconCard type="ai_turnouthq_chat_bot" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_TURNOUTHQ_CHAT_BOT' }),
              tab: 'AI_TURNOUTHQ_CHAT_BOT',
              pageName: 'AiChatBotRulebookPage',
              params: { rulebookId: PRIMARY_RULEBOOK_GROUP_ID, sessionId: uuidv4() },
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
          ]
          : [
            {
              Icon: <ProfileIconCard type="ai_turnouthq_chat_bot" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_TURNOUTHQ_CHAT_BOT' }),
              tab: 'AI_TURNOUTHQ_CHAT_BOT',
              pageName: 'AiChatBotRulebookPage',
              params: { rulebookId: PRIMARY_RULEBOOK_GROUP_ID, sessionId: uuidv4() },
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
          ]),
        {
          text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_REQUEST_RULEBOOK_BUTTON' }),
          tab: 'AI_REQUEST_RULEBOOK_BUTTON',
          pageName: false,
          type: 'Button',
          disabled: false,
          onClick: () => {
            onSiderbarChange(false);
            if (!isUserEmailVerified) {
              return history.push(
                createResourceLocatorString(
                  'SignUpStepsPage',
                  routeConfiguration,
                  { tab: SIGNUP_TAB_DETAIL },
                  {}
                )
              );
            }
            if (!userId) {
              return history.push('/login');
            }
            onOpenRuleBookModal();
          },
          redirectionType: REDIRECTION_TYPE_BUTTON,
        },
        {
          text: '',
          tab: 'UNDERLINE',
        },
        ...(userId
          ? [
            {
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_MY_FAVORITES' }),
              tab: 'AI_MY_FAVORITES',
              redirectionType: REDIRECTION_TYPE_SUB_TABS,
              subTabs: aiFavouriteRuleBook,
              isLast: true,
              tabLevel: 2,
            },

            {
              text: '',
              tab: 'UNDERLINE',
            },

            {
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.AI_GROUP' }),
              tab: 'AI_GROUP',
              redirectionType: REDIRECTION_TYPE_SUB_TABS,
              isLast: true,
              subTabs: aiRecentRuleBook,
              tabLevel: 2,
            },
          ]
          : [
            {
              Icon: <ProfileIconCard type="join_now" />,
              tab: 'JOIN_NOW_AI',
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.JOIN_NOW' }),
              pageName: 'SignupPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
            {
              Icon: <ProfileIconCard type="join_now" />,
              tab: 'LOGIN_AI',
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.LOGIN' }),
              pageName: 'LoginPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
            },
          ]),
      ],
      pages: ['AiChatBotPage', 'AiChatBotRulebookPage', 'RuleBooksSearchPage', 'FavouritePage'],
    },
    ...(userId
      ? [
        {
          Icon: <ProfileIconCard type="directory" />,
          tab: 'DIRECTORY',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.DIRECTORY' }),
          redirectionType: REDIRECTION_TYPE_LINK,
          pageName: 'AlgoliaDirectorySearchPage',
          params: {},
          search: {},
        },
      ]
      : []),
    // Public Daily Deals — available logged-out (teaser) and logged-in (full list).
    {
      Icon: <ProfileIconCard type="discount_bottom_mobile_svg" />,
      tab: 'DISCOUNTS',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.DISCOUNTS' }),
      redirectionType: REDIRECTION_TYPE_LINK,
      pageName: 'DiscountsLandingPage',
      params: {},
      search: {},
    },
    ...(userId
      ? [
        {
          Icon: <Avatar className={css.avatar} user={currentUser} disableProfileLink />,
          tab: 'PROFILE',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE' }),
          subLabel: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_SUB' }),
          redirectionType: REDIRECTION_TYPE_LINK,
          pageName: 'ProfileSettingsPage',
          params: {},
          search: {},
          tabLevel: 1,
          subTabs: [
            {
              Icon: <Avatar className={css.avatar} user={currentUser} disableProfileLink />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_VIEW_PROFILE' }),
              tab: 'PROFILE_VIEW_PROFILE_PAGE',
              pageName: 'ProfilePage',
              params: { id: userId },
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage === 'ProfilePage',
            },
            {
              Icon: <ProfileIconCard type="community_activity" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_EDIT' }),
              tab: 'PROFILE_PROFILE_SETTINGS_PAGE',
              pageName: 'ProfileSettingsPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage === 'ProfileSettingsPage',
            },
            {
              Icon: <ProfileIconCard type="shop" />,
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_MEMBERSHIP' }),
              tab: 'PROFILE_MEMBERSHIP',
              pageName: 'StripeSubscriptionPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage === 'StripeSubscriptionPage',
            },
            {
              Icon: <ProfileIconCard type="youth_badge" />,
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_YOUTH_ACCOUNT_PAGE',
              }),
              tab: 'PROFILE_ACCOUNT_SETTINGS_YOUTH_ACCOUNT_PAGE',
              pageName: 'YouthAccountSettingsPage',
              params: {},
              search: {},
              redirectionType: REDIRECTION_TYPE_LINK,
              isActive: currentPage === 'YouthAccountSettingsPage',
            },
            {
              text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_LOG_OUT' }),
              tab: 'PROFILE_REQUEST_GROUP_BUTTON',
              pageName: false,
              type: 'Button',
              disabled: false,
              onClick: () => {
                onLogout();
              },
              redirectionType: REDIRECTION_TYPE_BUTTON,
            },
            ...(shouldShowUpgradeButton
              ? [
                {
                  text: intl.formatMessage({ id: 'MobileHamburgerNavigation.PROFILE_UPGRADE' }),
                  tab: 'PROFILE_UPGRADE',
                  pageName: 'StripeSubscriptionPage',
                  params: {},
                  search: {},
                  type: 'UpgradeButton',
                  redirectionType: REDIRECTION_TYPE_LINK,
                  isActive: currentPage === 'StripeSubscriptionPage',
                },
              ]
              : []),
            {
              text: '',
              tab: 'UNDERLINE',
            },
            {
              text: intl.formatMessage({
                id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PAGE',
              }),
              tab: 'PROFILE_ACCOUNT_SETTINGS_PAGE',
              redirectionType: REDIRECTION_TYPE_SUB_TABS,
              isLast: true,
              tabLevel: 2,
              subTabs: [
                {
                  text: intl.formatMessage({
                    id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_CONTACT_DETAILS',
                  }),
                  tab: 'PROFILE_ACCOUNT_SETTINGS_CONTACT_DETAIL_PAGE',
                  pageName: 'ContactDetailsPage',
                  params: {},
                  search: {},
                  redirectionType: REDIRECTION_TYPE_LINK,
                },
                {
                  text: intl.formatMessage({
                    id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PASSWORD_CHANGE_PAGE',
                  }),
                  tab: 'PROFILE_ACCOUNT_SETTINGS_PASSWORD_CHANGE_PAGE',
                  pageName: 'PasswordChangePage',
                  params: {},
                  search: {},
                  redirectionType: REDIRECTION_TYPE_LINK,
                },
                ...(isBusiness
                  ? [
                    {
                      text: intl.formatMessage({
                        id: 'MobileHamburgerNavigation.PROFILE_PROFILE_GENERAL_HOURS_PAGE',
                      }),
                      tab: 'PROFILE_PROFILE_GENERAL_HOURS_PAGE',
                      pageName: 'GeneralHourPage',
                      params: {},
                      search: {},
                      redirectionType: REDIRECTION_TYPE_LINK,
                      isActive: currentPage == 'GeneralHourPage',
                    },
                  ]
                  : []),
                ...(isSeller
                  ? [
                    {
                      text: intl.formatMessage({
                        id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_SHIPPING_PAGE',
                      }),
                      tab: 'PROFILE_ACCOUNT_SETTINGS_SHIPPING_PAGE',
                      pageName: 'ShippingInfoPage',
                      params: {},
                      search: {},
                      redirectionType: REDIRECTION_TYPE_LINK,
                    },
                    {
                      text: intl.formatMessage({
                        id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PAYOUT_PAGE',
                      }),
                      tab: 'PROFILE_ACCOUNT_SETTINGS_PAYOUT_PAGE',
                      pageName: 'StripePayoutPage',
                      params: {},
                      search: {},
                      redirectionType: REDIRECTION_TYPE_LINK,
                    },
                    {
                      text: intl.formatMessage({
                        id:
                          'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_PAYMENT_METHOD_PAGE',
                      }),
                      tab: 'PROFILE_ACCOUNT_SETTINGS_PAYMENT_METHOD_PAGE',
                      pageName: 'PaymentMethodsPage',
                      params: {},
                      search: {},
                      redirectionType: REDIRECTION_TYPE_LINK,
                    },
                  ]
                  : []),
                // Group Payment History
                {
                  text: intl.formatMessage({
                    id:
                      'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_GROUP_PAYMENT_HISTORY_PAGE',
                  }),
                  tab: 'PROFILE_ACCOUNT_SETTINGS_GROUP_PAYMENT_HISTORY_PAGE',
                  pageName: 'GroupPaymentHistoryPage',
                  params: { tab: GROUP_PAYMENT_PAGE_PAYMENT_HISTORY },
                  search: {},
                  redirectionType: REDIRECTION_TYPE_LINK,
                },
                {
                  text: intl.formatMessage({
                    id: 'MobileHamburgerNavigation.PROFILE_ACCOUNT_SETTINGS_DELETE_PAGE',
                  }),
                  tab: 'PROFILE_ACCOUNT_SETTINGS_DELETE_PAGE',
                  pageName: 'DeleteAccountPage',
                  params: {},
                  search: {},
                  redirectionType: REDIRECTION_TYPE_LINK,
                },
              ],
            },
            ...(isOrganizationAdmin ||
              isSupabaseAdmin ||
              isWalletAdmin ||
              isModAdmin ||
              isUserAdmin ||
              isShopifyAdmin ||
              isHighlightAdmin ||
              isDiscoverGroupsCarouselAdmin ||
              isSubscriptionAdmin
              ? [
                {
                  text: '',
                  tab: 'UNDERLINE',
                },
                {
                  text: intl.formatMessage({
                    id: 'MobileHamburgerNavigation.PROFILE_ADMIN_PAGE',
                  }),
                  tab: 'PROFILE_ADMIN_PAGE',
                  redirectionType: REDIRECTION_TYPE_SUB_TABS,
                  isLast: true,
                  tabLevel: 2,
                  subTabs: [
                    ...(isOrganizationAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id:
                              'LayoutWrapperAccountSettingsSideNav.AdminPanelOrganisationPageTabTitle',
                          }),
                          pageName: 'AdminPanelOrganisationPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isSupabaseAdmin
                      ? [
                        {
                          text: intl.formatMessage({ id: 'TopbarDesktop.adminPanelLink' }),
                          tab: 'PROFILE_ADMIN_PAGE_SUPABASE',
                          pageName: 'AdminPanelSupabasePage',
                          params: { tab: 'general' },
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isModAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id: 'TopbarDesktop.adminPanelModeratorLink',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_MODERATOR',
                          pageName: 'AdminPanelModeratorsPage',
                          params: { tab: 'general' },
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isWalletAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelWalletPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_WALLET',
                          pageName: 'AdminPanelWalletPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    // ...(isShopifyAdmin
                    //   ? [
                    //     {
                    //       text: intl.formatMessage({
                    //         id:
                    //           'LayoutWrapperAccountSettingsSideNav.AdminPanelShopifyFeeReportPage',
                    //       }),
                    //       tab: 'PROFILE_ADMIN_PAGE_SHOPIFY_FEE_REPORT',
                    //       pageName: 'AdminPanelShopifyFeeReportPage',
                    //       params: {},
                    //       search: {},
                    //       redirectionType: REDIRECTION_TYPE_LINK,
                    //     },
                    //   ]
                    //   : []),
                    ...(isUserAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelUserPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_USER',
                          pageName: 'AdminPanelUserPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isHighlightAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelHighlightsPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_HIGHLIGHTS',
                          pageName: 'AdminPanelHighlightsPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isDiscoverGroupsCarouselAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id:
                              'LayoutWrapperAccountSettingsSideNav.AdminPanelDiscoverGroupsCarouselsPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_DISCOVER_GROUPS_CAROUSELS',
                          pageName: 'AdminPanelDiscoverGroupsCarouselsPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isDiscountAdmin
                      ? [
                        {
                          text: 'Discount Codes',
                          tab: 'PROFILE_ADMIN_PAGE_DISCOUNT_CODES',
                          pageName: 'AdminPanelDiscountCodesPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                    ...(isSubscriptionAdmin
                      ? [
                        {
                          text: intl.formatMessage({
                            id: 'LayoutWrapperAccountSettingsSideNav.AdminPanelSubscriptionsPage',
                          }),
                          tab: 'PROFILE_ADMIN_PAGE_SUBSCRIPTIONS',
                          pageName: 'AdminPanelSubscriptionsPage',
                          params: {},
                          search: {},
                          redirectionType: REDIRECTION_TYPE_LINK,
                        },
                      ]
                      : []),
                  ],
                },
              ]
              : []),
          ],
          pages: [
            'ProfilePage',
            'ProfileSettingsPage',
            'PasswordRecoveryPage',
            'ContactDetailsPage',
            'PasswordChangePage',
            'ShippingInfoPage',
            'YouthAccountSettingsPage',
            'StripePayoutPage',
            'StripePayoutOnboardingPage',
            'PaymentMethodsPage',
            'StripeSubscriptionPage',
            'GroupPaymentHistoryPage',
            'DeleteAccountPage',
            'AdminPanelOrganisationPage',
            'AdminPanelModeratorsPage',
            'AdminPanelUserPage',
            'AdminPanelWalletPage',
            'AdminPanelShopifyFeeReportPage',
            'AdminPanelSupabasePage',
            'AdminPanelHighlightsPage',
            'AdminPanelHighlightCategoryPage',
            'AdminPanelDiscoverGroupsCarouselsPage',
            'AdminPanelSubscriptionsPage',
            'DeleteAccountPage',
            'GeneralHourPage',
          ],
        },
      ]
      : []),
    ...(userId
      ? [
        {
          text: '',
          tab: 'UNDERLINE',
        },
      ]
      : []),
    ...(!isYouthRestricted && userId
      ? [
        {
          Icon: <ProfileIconCard type="order" />,
          tab: 'ORDER_PAGE',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.ORDER_PAGE' }),
          pageName: 'InboxBasePage',
          params: {},
          notificationCount: notificationCount + currentUserUnreadInquiriesCount || 0,
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        {
          Icon: <ProfileIconCard type="shop" />,
          tab: 'CART_PAGE',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.CART_PAGE' }),
          pageName: 'CartPage',
          params: {},
          notificationCount: cartCount,
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
      ]
      : []),
    ...(userId
      ? [
        {
          Icon: <ProfileIconCard type="notification_mobile" />,
          tab: 'NOTIFICATIONS',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.NOTIFICATIONS' }),
          notificationCount: totalNotificationCount,
          redirectionType: REDIRECTION_TYPE_BUTTON,
          onClick: () => {
            onSiderbarChange(false);
            if (!isUserEmailVerified) {
              return history.push(
                createResourceLocatorString(
                  'SignUpStepsPage',
                  routeConfiguration,
                  { tab: SIGNUP_TAB_DETAIL },
                  {}
                )
              );
            }
            openNotificationModal();
          },
        },
      ]
      : []),
    {
      text: '',
      tab: 'UNDERLINE',
    },
    {
      Icon: <ProfileIconCard type="faq_mobile" />,
      tab: 'FAQ',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.FAQ' }),
      pageName: 'CMSPage',
      params: { pageId: 'faq' },
      search: {},
      redirectionType: REDIRECTION_TYPE_LINK,
    },
    {
      Icon: <ProfileIconCard type="support_bottom_mobile_svg" />,
      tab: 'SUPPORT',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.SUPPORT' }),
      redirectionType: REDIRECTION_TYPE_BUTTON,
      onClick: () => {
        onSiderbarChange(false);
        if (!userId) {
          return history.push('/login');
        }

        openIntecomOnClickEvents();
      },
    },
    {
      Icon: <ProfileIconCard type="turnoutai_bottom_mobile_svg" />,
      tab: 'BLOG',
      label: intl.formatMessage({ id: 'MobileHamburgerNavigation.BLOG' }),
      redirectionType: REDIRECTION_TYPE_BUTTON,
      onClick: () => {
        if (typeof window !== 'undefined' && window) {
          window.open(`${process.env.REACT_APP_GHOST_APP_URL}`, '_self');
        }
      },
    },
    ...(userId
      ? [
        {
          Icon: <Avatar className={css.avatar} user={currentUser} disableProfileLink />,
          tab: 'LOGOUT',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.LOGOUT' }),
          redirectionType: REDIRECTION_TYPE_BUTTON,
          onClick: () => {
            onLogout();
          },
        },
      ]
      : []),
    ...(!userId
      ? [
        {
          Icon: <ProfileIconCard type="join_now" />,
          tab: 'JOIN_NOW',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.JOIN_NOW' }),
          pageName: 'SignupPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
        {
          Icon: <ProfileIconCard type="join_now" />,
          tab: 'LOGIN',
          label: intl.formatMessage({ id: 'MobileHamburgerNavigation.LOGIN' }),
          pageName: 'LoginPage',
          params: {},
          search: {},
          redirectionType: REDIRECTION_TYPE_LINK,
        },
      ]
      : []),
  ];

  const navigateToPage = section => {
    const { tab, pageName, params = {}, search = {} } = section;
    if (pageName) {
      onSiderbarChange(false); // Close the sidebar after navigation
      history.push(createResourceLocatorString(pageName, routeConfiguration, params, search));
      pageName == 'SearchPage' && search?.hierarchicalCategories && window
        ? window?.location?.reload()
        : null;
      return;
    } else {
      // If the section does not have a pageName, just log the tab
      console.log(`Navigating to tab: ${tab}`);
    }
  };

  const handleMenuClick = section => {
    const { redirectionType, subTabs, onClick, type, isLast = false, tabLevel } = section;

    // For top-level sections (tabLevel === 1) with REDIRECTION_TYPE_LINK, navigate directly
    if (redirectionType === REDIRECTION_TYPE_LINK && tabLevel === 1) {
      navigateToPage(section);
      return;
    }

    if (redirectionType === REDIRECTION_TYPE_SUB_TABS && subTabs && subTabs.length && !isLast) {
      // If the section has subTabs, open the subMenu
      setSubMenuOpen(section);
      return;
    }

    if (redirectionType === REDIRECTION_TYPE_LINK) {
      // If the section is a link, navigate to the corresponding page
      navigateToPage(section);
      return;
    }

    if (redirectionType === REDIRECTION_TYPE_BUTTON && onClick && type != 'button') {
      // If the section is a button, handle the button click
      onClick();
      onSiderbarChange(false);
      return;
    }
  };

  const handleButtonClick = item => {
    if (item.onClick) {
      item.onClick();
      onSiderbarChange(false);
    }
  };

  const renderDivider = key => <div className={css.underline} key={key} />;

  const renderButton = (item, key) => (
    <button
      className={css.menuButton}
      disabled={item.disabled}
      key={key}
      onClick={() => handleButtonClick(item)}
    >
      {item.text}
    </button>
  );

  const renderUpgradeButton = (item, key) => (
    <button
      className={css.upgradeButton}
      key={key}
      onClick={() => {
        onSiderbarChange(false);
        navigateToPage(item);
      }}
    >
      {item.text}
    </button>
  );

  const renderCreatePostButton = (item, key) => (
    <button
      className={css.createPostButton}
      disabled={item.disabled}
      key={key}
      onClick={() => handleButtonClick(item)}
    >
      {item.text}
    </button>
  );

     const renderShopifyButton = (item, key) => (
    <button
      className={css.shopifyButton}
      disabled={item.disabled}
      key={key}
      onClick={() => handleButtonClick(item)}
    >
      {item.text}
    </button>
  );

  const renderSortButton = (item, key) => (
    <div className={css.sortButtonWrapper} key={key}>
      <button
        className={css.sortButton}
        onClick={item.onClick}
      >
        {item.text}
      </button>
    </div>
  );

  const renderNoResult = (item, key) => (
    <div className={css.noResultMessage} key={key}>
      {item.text}
    </div>
  );

  const renderMenuItem = (item, key, isLast) => {
    // Check if this category is currently selected
    const currentSearchParams = typeof window !== 'undefined' ? parse(window?.location?.search || '') : {};
    const selectedCategory = currentSearchParams?.hierarchicalCategories;
    const isSelected = selectedCategory && item.search?.hierarchicalCategories === selectedCategory;

    return (
      <>
        <div
          key={key}
          className={isLast ? css.menuSectionInnerLast : css.menuSectionInner}
          onClick={() => handleMenuClick(item)}
        >
          {item.Icon ? <div className={css.menuItemIcon}>{item.Icon}</div> : null}
          <div className={isSelected ? css.tabnameSelected : (item.subTabs ? css.tabnameActive : css.tabname)}>
            {typeof item.text === 'string' ? item.text : item.text?.props?.id}
          </div>
          {item.sortButton ? (
            <button
              className={css.sortButtonInline}
              onClick={(e) => {
                e.stopPropagation();
                item.sortButton.onClick();
              }}
            >
              {item.sortButton.text}
              <ProfileIconCard type="sort" />
            </button>
          ) : null}
          {item.notificationCount && item.notificationCount > 0 ? (
            <div className={css.notificationCount}>{item.notificationCount}</div>
          ) : null}
        </div>
        {item.searchControl ? (
          <div className={css.searchInline} onClick={e => e.stopPropagation()}>
            <input
              className={css.joinedGroupsSearchInputInline}
              placeholder={intl.formatMessage({ id: 'MobileHamburgerNavigation.SEARCHFORGROUPS', defaultMessage: 'Search for groups' })}
              value={joinedGroupsQuery}
              onChange={e => setJoinedGroupsQuery(e.target.value)}
            />
          </div>
        ) : null}
        {item.subTabs ? (
          <div className={css.discoverItems}>{renderSubMenu(item.subTabs, true)}</div>
        ) : null}

      </>
    );
  };

  const renderSubMenu = (subTabs, isLast) => (
    <div className={css.subMenu}>
      {subTabs.map((subItem, subKey) => {
        if (subItem.tab === 'UNDERLINE') return renderDivider(subKey);
        // Special handling for 'See more' button in JoinedGroups and moderatorGroups
        if (
          ['COMMUNITY_MY_GROUP_SEE_MORE', 'COMMUNITY_GROUP_EXPLORE_SEE_MORE'].includes(
            subItem.tab
          ) &&
          subItem.type == 'Button'
        ) {
          return (
            <div
              className={css.seeMoreButton}
              onClick={() => {
                if (expandedTab == subItem.tab) setExpandedTab(null);
                else setExpandedTab(subItem.tab);
                console.log('See more clicked', subItem.tab);
                const newSubMenu = menuData.find(section => section.tab === subMenuOpen?.tab);
                console.log(newSubMenu, 'newSubMenu');
                setSubMenuOpen(newSubMenu);
              }}
              key={'key' + subItem.tab}
            >
              {subItem.text}
            </div>
          );
        }
        if (subItem.type === 'Button') return renderButton(subItem, subKey);
        if (subItem.type === 'UpgradeButton') return renderUpgradeButton(subItem, subKey);
        if (subItem.type === 'CreatePostButton') return renderCreatePostButton(subItem, subKey);
          if (subItem.type === 'ShopifyButton') return renderShopifyButton(subItem, subKey);
        if (subItem.type === 'SortButton') return renderSortButton(subItem, subKey);
        if (subItem.type === 'NoResult') return renderNoResult(subItem, subKey);
        return renderMenuItem(subItem, subKey, isLast);
      })}
    </div>
  );

  const renderSection = (section, key) => {
    if (section.tab === 'UNDERLINE') return renderDivider(key);
    if (section.type === 'Button') return renderButton(section, key);
    return (
      <div className={css.menuSection} key={key} onClick={() => handleMenuClick(section)}>
        <div className={css.menuItem}>
          {section.Icon ? <div className={css.menuItemIcon}>{section.Icon}</div> : null}
          <div className={css.tabname}>{section.label}</div>
          {section.subTabs || section.label == "Directory" ? (
            <div className={css.rightArrow}>
              {' '}
              <IconCollection name="sidenav_arrow" />
            </div>
          ) : null}
          {section.notificationCount && section.notificationCount > 0 ? (
            <div className={css.notificationCount}>{section.notificationCount}</div>
          ) : null}
        </div>
      </div>
    );
  };

  const goToSearchPage = () => {
    if (query && query.trim() && typeof window !== 'undefined' && window) {
      onSiderbarChange(false);
      const previousQueryParams = parse(window?.location?.search || '');
      return history.push(
        createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          { ...previousQueryParams, query: query.trim() }
        )
      );
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={classNames(css.overlay, { [css.show]: isSiderbarOpen })}
        onClick={() => onSiderbarChange(false)}
      ></div>

      {/* Sidebar */}
      <div className={classNames(css.sidebar, { [css.open]: isSiderbarOpen })}>
        {subMenuOpen ? null : (
          <div className={subMenuOpen ? css.sidebarSearchHeaderOpen : css.sidebarSearchHeader}>
            <span className={css.mainMenuTextFront}>
              {intl.formatMessage({ id: 'MobileHamburgerNavigation.MAIN_MENU' })}
            </span>
            {/* <span>{intl.formatMessage({ id: 'MobileHamburgerNavigation.SEARCH' })}</span> */}
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => {
                setSubMenuOpen(null);
                onSiderbarChange(false);
              }}
            >
              <IconCollection name="sidenav_close" />
            </button>
          </div>
        )}
        {subMenuOpen ? null : renderDivider('MAIN_MENU')}
        {subMenuOpen ? (
          <div className={css.sidebarHeader} onClick={() => setSubMenuOpen(null)}>
            <button type="button" aria-label="Back to main menu">
              <IconCollection name="sidenav_back" />
            </button>
            <span className={css.mainMenuText}>
              {intl.formatMessage({ id: 'MobileHamburgerNavigation.MAIN_MENU' })}
            </span>
            <button
              type="button"
              aria-label="Close menu"
              className={css.closeButton}
              onClick={() => {
                // setSubMenuOpen(null);
                onSiderbarChange(false);
              }}
            >
              <IconCollection name="sidenav_close" />
            </button>
          </div>
        ) : null}

        {subMenuOpen && subMenuOpen.subLabel ? (
          <div className={css.subMenuOpenSubLabel}>{subMenuOpen.subLabel}</div>
        ) : null}

        {subMenuOpen
          ? renderSubMenu(
            Array.isArray(menuData.find(st => st.tab === subMenuOpen?.tab)?.subTabs)
              ? menuData.find(st => st.tab === subMenuOpen?.tab)?.subTabs
              : []
          )
          : menuData.map((section, index) => renderSection(section, index))}
        <div className={css.sidebarFooter}></div>
      </div>

      {/* Menu Icon */}
      <div
        className={css.menuIcon}
        data-testid="mobile-menu-toggle"
        onClick={e => {
          e.preventDefault();
          onSiderbarChange(!isSiderbarOpen);

          // If user is on their own profile page, open profile submenu directly
          // This provides quick access to profile settings and related options
          if (isOnOwnProfile && userId) {
            const profileSection = menuData.find(section => section.tab === 'PROFILE');
            setSubMenuOpen(profileSection);
          } else {
            // Otherwise, open to the current page's submenu or main menu
            setSubMenuOpen(currentPage ? getSubMenu(menuData, currentPage) : null);
          }

          setExpandedTab(null);
        }}
      >
        <MenuIcon />{' '}
      </div>
    </>
  );
};

export default MobileHamburgerNavigation;
